import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { cutString, getBasicPath } from "../../../../helpers";
import { 
  IconButton, 
  Typography, 
  Box, 
  Menu, 
  MenuItem, 
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { showSuccessToast } from "../../../../toast/toast-service";
import { useIsMobile } from "../../../../hooks/state-checkers/use-is-mobile";
import { useGetCourseFromUrl } from "../../../../hooks/use-get-course-from-url";
import { useStore } from "../../../../store/store";
import { GET_USER_COURSES } from "../../../../apollo-client/queries";

export default function MyCourse({
  courseId,
  course,
  userId,
  userCourses,
  isCourseNotAdded,
  removeMyCourses,
  closeCourseNav,
}) {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isMobileMUI = useMediaQuery(theme.breakpoints.down('md'));
  const deleteSuccessMessage =
    cutString(course.title, 20) +
    " " +
    intl.formatMessage({
      id: "general.delete-success",
    });

  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleteRevealed, setIsDeleteRevealed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const courseIdFromPath = useGetCourseFromUrl();
  const touchStartXRef = useRef(null);
  const dragThreshold = 60; // pixels to drag to reveal delete icon

  const { menuIsOpen, setMenuIsOpen } = useStore();

  // Clean up state when component unmounts
  useEffect(() => {
    return () => {
      if (menuIsOpen) {
        setMenuIsOpen(false);
      }
    };
  }, [menuIsOpen, setMenuIsOpen]);

  const resetPosition = () => {
    setDragOffset(0);
    setIsDeleteRevealed(false);
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuIsOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIsOpen(false);
  };

  const handleTouchStart = (event) => {
    if (isMobile && !isCourseNotAdded) {
      touchStartXRef.current = event.touches[0].clientX;
      setIsDragging(true);
    }
  };

  const handleTouchMove = (event) => {
    if (isMobile && !isCourseNotAdded && touchStartXRef.current !== null) {
      const currentX = event.touches[0].clientX;
      const deltaX = touchStartXRef.current - currentX;

      // Allow both left and right swiping
      const newOffset = Math.max(-dragThreshold, Math.min(deltaX, dragThreshold));
      setDragOffset(newOffset);

      if (newOffset >= dragThreshold && !isDeleteRevealed) {
        setIsDeleteRevealed(true);
        setDragOffset(dragThreshold);
      } else if (newOffset <= -dragThreshold / 2) {
        // Swipe right to hide
        resetPosition();
      }
    }
  };

  const handleTouchEnd = () => {
    if (isMobile && !isCourseNotAdded) {
      setIsDragging(false);

      if (dragOffset < dragThreshold / 2 && dragOffset > -dragThreshold / 2) {
        // Snap back if not dragged enough in either direction
        resetPosition();
      } else if (dragOffset >= dragThreshold) {
        // Snap to revealed position
        setDragOffset(dragThreshold);
        setIsDeleteRevealed(true);
      }

      touchStartXRef.current = null;
    }
  };

  const handleCourseClick = () => {
    // If delete is revealed, clicking the course content should hide it
    if (isDeleteRevealed) {
      resetPosition();
      return;
    }
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    try {
      await removeMyCourses({
        variables: { ids: [course._id] },
      });
      showSuccessToast(deleteSuccessMessage);
      // Reset the swipe state after deletion
      resetPosition();
      handleMenuClose();
    } catch (error) {
      console.error('Error removing course:', error);
      // Optionally show error toast
    }
  };

  return (
    <Card
      id={"wrapper_" + course._id}
      data-testid={`my-course-${course._id}`}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: courseIdFromPath === course._id ? 'action.selected' : 'background.paper',
        border: courseIdFromPath === course._id ? '2px solid' : '1px solid',
        borderColor: courseIdFromPath === course._id ? 'primary.main' : 'divider',
        transform: `translateX(${dragOffset}px)`,
        transition: isDragging ? 'none' : 'transform 0.3s ease, all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: courseIdFromPath === course._id ? 'action.selected' : 'action.hover',
          transform: `translateX(${dragOffset}px) translateY(-1px)`,
          boxShadow: 2,
        },
        overflow: 'hidden'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <CardContent 
        sx={{ 
          p: 2, 
          '&:last-child': { pb: 2 },
          position: 'relative'
        }}
        onClick={handleCourseClick}
      >
        <Link
          title={course.title}
          to={`/courses/${course._id}/${getBasicPath(location.pathname)}${
            isMobile ? "" : "?mycourseopen=1"
          }`}
          style={{ 
            textDecoration: 'none', 
            color: 'inherit',
            display: 'block',
            width: '100%'
          }}
          onClick={() => {
            if (isMobile) {
              closeCourseNav();
            }
          }}
        >
          <Typography 
            variant="body1" 
            fontWeight={500}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary'
            }}
          >
            {course.title}
          </Typography>
        </Link>

        {!isCourseNotAdded && (
          <>
            {/* Mobile: Delete button revealed by swipe */}
            {isMobileMUI && (
              <IconButton
                data-testid={`my-course-delete-button-${course.title}`}
                size="small"
                onClick={handleDelete}
                aria-label="Delete course"
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  visibility: isDeleteRevealed ? 'visible' : 'hidden',
                  color: 'error.main',
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                  transition: 'visibility 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.2)',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}

            {/* Desktop: Menu button with dropdown */}
            {!isMobileMUI && (
              <>
                <IconButton
                  data-testid={`my-course-menu-icon-${course.title}`}
                  size="small"
                  onClick={handleMenuClick}
                  aria-label="Menu"
                  aria-controls={anchorEl ? "course-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorEl ? "true" : undefined}
                  sx={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    visibility: 'hidden',
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '.MuiCard-root:hover &': {
                      visibility: 'visible',
                    }
                  }}
                >
                  <MoreVertIcon />
                </IconButton>

                <Menu
                  id="course-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  disableScrollLock={true}
                  keepMounted={false}
                  PaperProps={{
                    sx: {
                      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.4)',
                      borderRadius: 1,
                      minWidth: 100,
                    }
                  }}
                >
                  <MenuItem
                    onClick={handleDelete}
                    data-testid={`my-course-delete-button-${course.title}`}
                    sx={{
                      fontSize: 14,
                      py: 1,
                      px: 2.5,
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    {intl.formatMessage({ id: "general.delete" })}
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

